'use client';

import { Upload } from 'lucide-react';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  ConversionChoiceDialog,
  type ConverterChoice,
  type ConverterSuggestion,
} from './ConversionChoiceDialog';
import type { DoclingVlmMode } from './doclingTier';
import { useDocumentsStore } from './documentsStore';

const ACCEPTED_EXTS = new Set([
  '.pdf', '.txt', '.md', '.docx', '.doc',
  '.csv', '.json', '.xml', '.html', '.htm', '.rtf',
  '.epub', '.mobi',
]);

const ACCEPTED_TYPES = [...ACCEPTED_EXTS].join(',');

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

function validateFile(t: ReturnType<typeof useTranslations>, file: File): string | null {
  const dotIdx = file.name.lastIndexOf('.');
  const ext = dotIdx !== -1 ? file.name.slice(dotIdx).toLowerCase() : '';
  if (!ext || !ACCEPTED_EXTS.has(ext)) {
    return ext
      ? t('documentUpload.unsupportedTypeExt', { ext })
      : t('documentUpload.unsupportedType');
  }
  if (file.size > MAX_FILE_SIZE) {
    return t('documentUpload.fileTooLarge', { size: (file.size / (1024 * 1024)).toFixed(1) });
  }
  return null;
}

/**
 * Build a *placeholder* Docling-Premium suggestion from the file's
 * extension. Wave 10 of the Docling Premium plan ships only the FE
 * chooser — the real sniffer.PreflightSuggest() output isn't yet wired
 * through a WS preflight roundtrip. For now we only show the pill on
 * PDFs (where Docling actually helps) with conservative defaults; the
 * server's RouteDecision still has the final say at conversion time
 * regardless of what the user clicks.
 *
 * TODO(wave-11+): replace with a `PreflightUploadRequest` WS roundtrip
 * that returns the real Suggestion incl. estimatedCredits/secs from the
 * actual sniffer.
 */
function placeholderSuggestion(file: File): ConverterSuggestion | null {
  const dotIdx = file.name.lastIndexOf('.');
  const ext = dotIdx !== -1 ? file.name.slice(dotIdx).toLowerCase() : '';
  if (ext !== '.pdf') return null;
  // Conservative estimate — server enforces the actual policy.
  const sizeMB = file.size / (1024 * 1024);
  const estPages = Math.max(1, Math.ceil(sizeMB * 8));
  const estimatedCredits = Math.max(1, estPages);
  const estimatedSecs = Math.max(5, Math.round(estPages * 0.4));
  return {
    suggested: true,
    reason: 'scanned_pdf',
    estimatedCredits,
    estimatedSecs,
  };
}

interface DocumentUploadProps {
  collectionId?: string;
  className?: string;
  /**
   * Tags to assign to uploads — the active scope's tags. Uploading while a
   * scope is selected tags the new document with these so it belongs to the
   * scope and appears in the (tag-filtered) scoped view immediately. Empty when
   * no scope is active.
   */
  scopeTags?: string[];
}

function DocumentUpload({ collectionId, className, scopeTags }: DocumentUploadProps) {
  const t = useTranslations('documents');
  const uploadDocument = useDocumentsStore((s) => s.actions.uploadDocument);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Pending-file state — a valid selected file opens the conversion-choice
  // dialog before the upload fires. Clearing it (after import or cancel) closes
  // the dialog. Every file type opens the dialog: for non-PDFs the Premium
  // option renders disabled, so the dialog still teaches what import does.
  const [pendingFile, setPendingFile] = React.useState<File | null>(null);

  const suggestion = React.useMemo<ConverterSuggestion | null>(
    () => (pendingFile ? placeholderSuggestion(pendingFile) : null),
    [pendingFile],
  );

  const handleFile = React.useCallback((file: File) => {
    setPendingFile(file);
  }, []);

  const startUpload = React.useCallback(
    (choice: ConverterChoice, vlmMode: DoclingVlmMode) => {
      if (!pendingFile) return;
      const file = pendingFile;
      setPendingFile(null);
      void uploadDocument(file, collectionId, choice, scopeTags, vlmMode);
    },
    [pendingFile, collectionId, uploadDocument, scopeTags],
  );

  const handleDrop = React.useCallback(
    (e: React.DragEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) {
        const error = validateFile(t, file);
        if (error) {
          toast.error(error);
          return;
        }
        handleFile(file);
      }
    },
    [t, handleFile],
  );

  const handleDragOver = React.useCallback((e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = React.useCallback((e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleClick = React.useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        const error = validateFile(t, file);
        if (error) {
          toast.error(error);
        } else {
          handleFile(file);
        }
      }
      // Reset so the same file can be selected again
      e.target.value = '';
    },
    [t, handleFile],
  );

  const handleCancelPending = React.useCallback(() => {
    setPendingFile(null);
  }, []);

  return (
    <div className={cn('w-full', className)}>
      <button
        type="button"
        className={cn(
          'flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border p-8 transition-colors cursor-pointer',
          'hover:border-accent hover:bg-hover',
          isDragOver && 'border-accent bg-hover',
        )}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <Upload className="h-8 w-8 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">
          {t('documentUpload.dragDrop')}
        </span>
        <span className="text-xs text-muted-foreground">
          {t('documentUpload.supportedFormats')}
        </span>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          className="hidden"
          onChange={handleInputChange}
        />
      </button>
      <ConversionChoiceDialog
        open={pendingFile !== null}
        file={pendingFile}
        suggestion={suggestion}
        onConfirm={startUpload}
        onCancel={handleCancelPending}
      />
    </div>
  );
}

export { DocumentUpload };
